/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  ThemeProvider,
  Autocomplete,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Typography
} from '@mui/material';
import { Formik } from 'formik';
import Grid2 from '@mui/material/Grid2';
import { getUsersByClinicId } from '../../services/apiService';
import { Clinician, Role, ROLES } from '../Surgery/oocyteRetrieval/OocyteRetrival.helper';
import { useAlert } from '../alertProvider';
import { useLoader } from '../loaderProvider/loader';
import { useClinic } from '../ClinicProvider';
import { embryoList, getPgtOrders, initialPgtOrder, PgtOrderValidationSchema, storeBiopsy, transferTypeOptions } from './pgtOrder.helper';
import { RequiredLabel } from '../../utils/commonUtils';
import { fetchViaProxy } from './pgtaHelper';
import { usePatientContext } from '../patinetProvider';
import { useTranslation } from 'react-i18next';
interface PgtOrderFormProps {
  onCancel: () => void;
}
 
const PgtOrderForm: React.FC<PgtOrderFormProps> = ({ onCancel }) => {

  const { t: tGeneral } = useTranslation('general');
  const { t: tPGT } = useTranslation('pgt_config');
  const { addAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const { ClinicId } = useClinic();
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const [embryologists, setEmbryologists] = useState<{ id: string; name: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [physicians, setPhysicians] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { patientId } = usePatientContext();

  const fetchBarcodes = useCallback(async () => {
    try {
      const response = await fetchViaProxy('/api/v2/barcodes', 'GET');      
      const rawData = response?.data;
      // Handle unexpected or malformed data
      if (!Array.isArray(rawData)) {
        console.warn('📛 Invalid barcode data structure: Not an array', rawData);
      }

      // Handle nested array case (e.g., [[...barcodes]])
      const barcodes = Array.isArray(rawData[0]) ? rawData[0] : rawData;
      if (!barcodes.length) {
        console.info('ℹBarcode list is empty');
      }
      console.log('✅ Parsed Barcodes:', barcodes);
      setBarcodes(barcodes);
    } catch (error) {
      console.error('Failed to fetch barcodes:', error);
    }
  }, []);

  const fetchEmbryologists = useCallback(async () => {
    try {
      const response = await fetchViaProxy('/api/v2/embryologists', 'GET');
      const rawData = response?.data;

      if (!Array.isArray(rawData) || typeof rawData[0] !== 'object') {
        console.warn('Unexpected structure for embryologists:', rawData);
        return;
      }

      const listObj = rawData[0]; // Correct: first (and only) object
      const parsedList = Object.entries(listObj).map(([id, name]) => ({
        id,
        name: String(name).trim(),
      }));

      console.log(' Parsed Embryologists:', parsedList);
      setEmbryologists(parsedList);
    } catch (error) {
      console.error(' Failed to fetch embryologists:', error);
    }
  }, []);

  const fetchPhysicians = useCallback(async () => {
    try {
      const response = await fetchViaProxy('/api/v2/doctors', 'GET');
      const rawData = response?.data?.[0] || {};

      // Transform the object into an array
      const parsed = Object.entries(rawData).map(([id, name]) => ({
        id,
        name: String(name).trim(),
      }));

      setPhysicians(parsed); //  Now this is an array usable in Autocomplete
      console.log(' Fetched Physicians:', parsed);
      
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
    }
  }, []);

  useEffect(() => {
    fetchBarcodes();
    fetchEmbryologists();
    fetchPhysicians();
  }, [fetchBarcodes, fetchEmbryologists, fetchPhysicians]); 

  useEffect(() => {
    const fetchOrders = async () => {
      if (!patientId) return;
      setLoading(true);
      try {
        const response = await getPgtOrders(patientId, 'PROGENESIS');
        const objects = response.objects || [];
        // Calculate rank based on ai_score (highest = #1)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const withScore = objects.map((o: any, idx: number) => ({
          ...o,
          _ai_score_num: o.ai_score !== undefined && o.ai_score !== null && o.ai_score !== '' ? Number(o.ai_score) : null,
          _originalIndex: idx,
        }));
        const sorted = [...withScore].sort((a, b) => {
          if (a._ai_score_num === null && b._ai_score_num === null) return 0;
          if (a._ai_score_num === null) return 1;
          if (b._ai_score_num === null) return -1;
          return b._ai_score_num - a._ai_score_num;
        });
        const rankMap = new Map();
        let rank = 1;
        for (const o of sorted) {
          if (o._ai_score_num !== null) {
            rankMap.set(o._originalIndex, `#${rank} Euploid Normal`);
            rank++;
          } else {
            rankMap.set(o._originalIndex, '-');
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const withRank = withScore.map((o: any) => {
          const { _originalIndex, ...rest } = o;
          return { ...rest, rank: rankMap.get(_originalIndex) };
        });
        setOrders(withRank);
      } catch (error) {
        setOrders([]);
        // Optionally log or show error
        // console.error('Failed to fetch PGT orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [patientId]);

  // Sanitize function to format payload before submission
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitizePgtOrderValues = (values: any, allEmbryos: any[]) => {
    const selectedEmbryosSet = new Set(values.selectedEmbryos);
    console.log('Sanitizing PGT Order Values:', values, 'Selected Embryos Set:', selectedEmbryosSet);

    const embryos = allEmbryos
      .filter((row) => selectedEmbryosSet.has(row.id))
      .map((row) => {
        console.log('Mapping Embryo Row:', row);  //  log here
        return {
          embryo_id: row.id,
          number_cells: row.number_cells,
          biopsy_type: 'day ' + row.day,
          performed_by: row.performed_by?.performed_by || row.performed_by,
          biopsy_date: values.date,
          embryo_grade: row.grade,
          tubing_by: row.tubing_by,
          witness_by: row.witness_by,
          biopsy_retrieval_date: values.date,
          pn_status: row.pn == null ? '' : row.pn,
          culture: row.action,
        };
      });


    return {
      ...values,
      embryos,
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePgtSubmit = (values:any) => {
    console.log('Submitted PGT Order Values:', values);

    const payload = sanitizePgtOrderValues(values, orders);
    console.log('Sanitized Payload:', payload);

    // Build final payload for API
    const apiPayload = {
      patient: patientId,
      clinic_id: ClinicId,
      barcode: payload.barcode,
      biopsy_date: payload.date,
      performed_by: payload.embryologist?.id || payload.embryologist?.id,
      physician: payload.physician,
      transfer_type: payload.transfer_type || 'fresh',
      embryos: payload.embryos,
      
      biopsy_retrieval_date: payload.date,
      test: 1,
    };

    showLoader();
    storeBiopsy(apiPayload, ClinicId)
      .then((response) => {
        hideLoader();
        // addAlert({ type: 'success', message: 'PGT order submitted successfully!' });
        console.log('PGT Order API response:', response);
        onCancel();
      })
      .catch((error) => {
        hideLoader();
        // addAlert({ type: 'error', message: 'Failed to submit PGT order.' });
        console.error('PGT Order API error:', error);
      });
  };


  return (
    <Formik
      initialValues={initialPgtOrder}
      validationSchema={PgtOrderValidationSchema}
      enableReinitialize
      onSubmit={handlePgtSubmit}
    >
      {({         
        values,
        setFieldValue,
        submitForm,
        validateForm,
        errors,
        dirty,  handleBlur, handleChange, handleSubmit, isSubmitting, touched, resetForm, setFieldTouched }) => (
        <form onSubmit={handleSubmit}>
          <Card sx={{ p: 1, boxShadow: 'none', border: 'none' }}>
            <CardContent>
              <Grid2 container spacing={2} justifyContent="start" sx={{ marginTop: 2 , marginRight:2}}>
                <Grid2 size={{ xs: 6, sm:4, md: 4, lg:3 }}>
                  <TextField
                    fullWidth
                    size="small" 
                    label={RequiredLabel(tGeneral('Date'), true)}
                    name="date"
                    type="date"
                    InputLabelProps={{shrink: true}}
                    value={values.date}
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('date', e.target.value || null)}
                    error={touched.date && Boolean(errors.date)}
                    // helperText={touched.date && errors.date}
                    aria-label="Date Input Field"
                  />
                </Grid2>

                {/* <Grid2 size={{ xs: 6, sm:4, md: 4, lg:3 }}>
                  <TextField
                    fullWidth
                    size="small" 
                    label={RequiredLabel(tGeneral('time'), true)}
                    name="time"
                    type="time"
                    InputLabelProps={{shrink: true}}
                    value={values.time}
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('time', e.target.value || null)}
                    error={touched.time && Boolean(errors.time)}
                    // helperText={touched.time && errors.time}
                    aria-label="Time Input Field"
                  />
                </Grid2> */}

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="barcode"
                    options={barcodes}
                    getOptionLabel={(option) => option}
                    value={values.barcode || null}
                    onChange={(_, value) => {
                      setFieldValue('barcode', value || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={tPGT('barcode', 'Barcode')}
                        error={touched.barcode && Boolean(errors.barcode)}
                        // helperText={touched.barcode && errors.barcode  }
                      />
                    )}
                  />
                </Grid2>                
                
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="physician"
                    options={physicians}
                    getOptionLabel={(option) => option.name}
                    value={physicians.find((p) => p.id === values.physician) || null}
                    onChange={(_, value) => setFieldValue('physician', value?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={tPGT('physician', 'Physician')}
                        error={touched.physician && Boolean(errors.physician)}
                        // helperText={touched.physician && errors.physician}
                      />
                    )}
                  />
                </Grid2>
                
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="embryologist"
                    options={embryologists}
                    getOptionLabel={(option) => option.name}
                    value={values.embryologist}
                    onChange={(_, value) => setFieldValue('embryologist', value)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={tPGT('Biopsy By', 'Biopsy By')} 
                        error={touched.embryologist && Boolean(errors.embryologist)}
                        
                      />
                    )}
                  />
                </Grid2>

                {/* <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="transfer_type"
                    options={transferTypeOptions}
                    getOptionLabel={(option) => tPGT(option.label)}
                    value={
                      transferTypeOptions.find((opt) => opt.value === values.transfer_type) || null
                    }
                    onChange={(_, selectedOption) => {
                      setFieldValue('transfer_type', selectedOption?.value || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={tPGT('transferType', 'Transfer Type')}
                        error={touched.transfer_type && Boolean(errors.transfer_type)}
                        // helperText={touched.transfer_type && errors.transfer_type}
                      />
                    )}
                  />
                </Grid2> */}

              </Grid2>

              <Box mt={3}>
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <CircularProgress />
                  </Box>
                ) : orders.length > 0 ? (
                  <Table sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: '#f5f5f5', // solid light gray
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }} />
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tPGT('embryoNo', 'Embryo No.')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tGeneral('day', 'Day')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tGeneral('stage', 'Stage')}</TableCell>
                        
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tGeneral('aiScore', 'AI Score')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tPGT('facility', 'Facility')}</TableCell>

                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tPGT('Biopsy By', 'Biopsy By')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tPGT('witness', 'Witness')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tPGT('rank', 'Rank')}</TableCell>


                        {/* <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{tPGT('plan', 'Plan')}</TableCell>  */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map((row) => {
                        const isSelected = values.selectedEmbryos?.includes(row.id);

                        return (
                          <TableRow
                            key={row.id}
                            hover
                            selected={isSelected}
                            sx={{
                              backgroundColor: isSelected
                                ? 'rgba(246, 94, 57, 0.1)'
                                : 'inherit',
                              transition: 'background-color 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              },
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                value={row.id}
                                checked={isSelected}
                                onChange={(_, checked) => {
                                  const selected = new Set(values.selectedEmbryos || []);
                                  if (checked) {
                                    selected.add(row.id);
                                  } else {
                                    selected.delete(row.id);
                                  }
                                  setFieldValue('selectedEmbryos', Array.from(selected));
                                }}
                                sx={{
                                  color: '#aaa',
                                  '&.Mui-checked': {
                                    color: '#f65e39',
                                  },
                                  ...(touched.selectedEmbryos &&
                                    Boolean(errors.selectedEmbryos) &&
                                    !isSelected && {
                                    border: '1px solid red',
                                    borderRadius: '4px',
                                  }),
                                }}
                              />
                            </TableCell>

                            <TableCell>{row.embryo_no}</TableCell>
                            <TableCell>{row?.day ?? '-'}</TableCell>
                            <TableCell>{row?.stage ?? '-'}</TableCell>
                            <TableCell>{row?.ai_score ?? '-'}</TableCell>
                            <TableCell>{row?.pgt_order?.pgt_facility ?? '-'}</TableCell>
                            <TableCell>{row?.embryologist ?? '-'}</TableCell>
                            <TableCell>{row?.witness ?? '-'}</TableCell>
                            <TableCell>{row?.rank ?? '-'}</TableCell>
                            {/* <TableCell>{row.action}</TableCell> */}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body1" color="textSecondary" align="center">
                    No data available.
                  </Typography>
                )}
              </Box>
              
              <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                <Button variant="outlined" onClick={onCancel}>{tGeneral('Cancel', 'Cancel')}</Button>
                <Button variant="contained" type="submit">{tPGT('scheduleBiopsy', 'Schedule Biopsy')}</Button>
              </Box>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
};

export default PgtOrderForm;